import { useEffect, useState } from "react";
import { getProperties, getPropertyMapPoints } from "../api";
import { LockedProperty, MapPoint, Property } from "../types";
import { describeLoadError, LoadError } from "../lib/apiErrors";
import {
  getCachedProperties,
  propertiesCacheGeneration,
  propertiesCacheKey,
  setCachedProperties,
} from "../lib/propertiesCache";
import { clearPersistedPins, persistPins, pinStoreGeneration, readPersistedPins } from "../lib/pinStore";

export interface CountyPropertiesState {
  properties: Property[];
  lockedProperties: LockedProperty[];
  /**
   * Map pins, available BEFORE the full rows. Filled from the slim
   * `?fields=map` request as soon as it lands, then replaced by the full
   * rows' own coordinates when they do. Only the map should read these — they
   * are not properties (no address / value / owner) and never feed a list,
   * filter, count or banner.
   */
  mapPoints: MapPoint[];
  /** Locked rows for the map's grey layer; same set the full response carries. */
  mapLockedProperties: LockedProperty[];
  totalCount: number;
  freeTierLimit: number;
  loading: boolean;
  /**
   * Human copy for the failed load, safe to render. NEVER axios's own
   * "Request failed with status code 403" (2026-10-13: a new user saw exactly
   * that). Use errorInfo to choose an action.
   */
  error: string | null;
  /** Why the load failed (status, server error code, the viewer's counties). */
  errorInfo: LoadError | null;
}

interface Args {
  countyId: string;
  userId: string | null | undefined;
  /** True while Supabase is still resolving the session — no fetch until false. */
  authLoading: boolean;
  /** Bumped by the dashboard to force a refetch (e.g. the resolved-rows toggle). */
  reloadKey: number;
}

const EMPTY_STATE: CountyPropertiesState = {
  properties: [],
  lockedProperties: [],
  mapPoints: [],
  mapLockedProperties: [],
  totalCount: 0,
  freeTierLimit: 0,
  loading: true,
  error: null,
  errorInfo: null,
};

// useCountyProperties loads the dashboard's property set for one county.
//
// Extracted from Dashboard.tsx for the load-time fix (2026-09-16) so its two
// new behaviors are testable:
//
//  1. Stale-while-revalidate. A county this user already opened in this tab
//     renders immediately from propertiesCache (no skeleton), while a fresh
//     request runs in the background and replaces it when it lands.
//  2. Map-first. On a cold county the full response is the slow one (server
//     fills + 100s of KB), so a second, slim request (`?fields=map`: same
//     gating and scope, pin-only rows) runs in parallel and lands first. It
//     feeds ONLY mapPoints / mapLockedProperties. `loading`, `properties`,
//     `totalCount` and `freeTierLimit` still come from the full response alone,
//     so the list, filters, counts and banners behave exactly as before.
//  3. No out-of-order overwrite. Before this, switching county A -> B while A
//     was still loading let A's late response replace B's rows. A response now
//     only applies if its request is still the current one.
//  4. Pins survive a page reload (2026-10-22). The slim response is persisted
//     (pinStore, IndexedDB) and read back on the next cold visit, so the map
//     draws last visit's pins immediately while both requests run. Slim
//     payloads only: the full rows never leave memory.
//
// Nothing fetches without a signed-in user (A1).
//
// Unchanged from before: nothing fetches until authLoading is false (#617 —
// an early request would go out without the bearer token), and the effect is
// keyed on the user id rather than the user object so a token refresh doesn't
// refetch.
export function useCountyProperties({ countyId, userId, authLoading, reloadKey }: Args): CountyPropertiesState {
  const [state, setState] = useState<CountyPropertiesState>(EMPTY_STATE);

  useEffect(() => {
    if (authLoading) return;
    // Logged-out visitors get the landing page, which never reads this data, so
    // they must not download the county (A1: 1.45 MB / 243 KB gz of mostly
    // locked teasers per visit). Reset instead of fetching so a sign-out also
    // drops the previous user's rows from state.
    if (!userId) {
      // A sign-out also drops the persisted pins (pinStore), so the next
      // person at this browser starts without them.
      clearPersistedPins();
      setState(EMPTY_STATE);
      return;
    }
    let current = true;
    const key = propertiesCacheKey(userId, countyId);
    const startedAt = propertiesCacheGeneration();
    const cached = getCachedProperties(key);

    if (cached) {
      setState({
        properties: cached.properties,
        lockedProperties: cached.lockedProperties,
        mapPoints: cached.properties,
        mapLockedProperties: cached.lockedProperties,
        totalCount: cached.totalCount,
        freeTierLimit: cached.freeTierLimit,
        loading: false,
        error: null,
        errorInfo: null,
      });
    } else {
      // A new request: the previous county's failure is no longer what's on
      // screen. Carrying it over showed county A's 403 while B was loading.
      setState((prev) => ({ ...prev, loading: true, error: null, errorInfo: null }));
    }

    // Slim map request. Skipped when a cached full response is already on
    // screen (its rows ARE the pins). Best-effort: a failure leaves the map
    // waiting for the full response, exactly as it did before this request
    // existed.
    let fullArrived = false;
    let slimArrived = false;
    if (!cached) {
      // Persisted pins from this user's last visit to the county (pinStore):
      // drawn at once on a page reload, then replaced by whichever response
      // lands first. Never applied once a fresher response is on screen.
      readPersistedPins(key).then((saved) => {
        if (!saved || !current || fullArrived || slimArrived) return;
        setState((prev) => ({
          ...prev,
          mapPoints: saved.points,
          mapLockedProperties: saved.lockedProperties,
        }));
      });

      const pinGeneration = pinStoreGeneration();
      getPropertyMapPoints(countyId)
        .then((slim) => {
          slimArrived = true;
          // Persist even if this request is no longer current, like the
          // in-memory cache below: it is still correct for ITS key.
          persistPins(key, { points: slim.points, lockedProperties: slim.lockedProperties }, pinGeneration);
          if (!current || fullArrived) return;
          setState((prev) => ({
            ...prev,
            mapPoints: slim.points,
            mapLockedProperties: slim.lockedProperties,
          }));
        })
        .catch(() => {});
    }

    getProperties(countyId)
      .then((resp) => {
        fullArrived = true;
        // Cache even if this request is no longer current: the response is
        // still correct for ITS key, and makes a later switch back instant.
        setCachedProperties(key, resp, startedAt);
        if (!current) return;
        setState({
          properties: resp.properties,
          lockedProperties: resp.lockedProperties,
          mapPoints: resp.properties,
          mapLockedProperties: resp.lockedProperties,
          totalCount: resp.totalCount,
          freeTierLimit: resp.freeTierLimit,
          loading: false,
          error: null,
          errorInfo: null,
        });
      })
      .catch((err: unknown) => {
        if (!current) return;
        // With cached rows already on screen, a failed background refresh
        // keeps them rather than replacing a working list with an error.
        if (cached) {
          setState((prev) => ({ ...prev, loading: false }));
          return;
        }
        const info = describeLoadError(err);
        setState((prev) => ({ ...prev, loading: false, error: info.message, errorInfo: info }));
      });

    return () => {
      current = false;
    };
  }, [countyId, userId, authLoading, reloadKey]);

  return state;
}
